import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { times as api, type TimeNaLista } from '../api/times'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import './times.css'

/**
 * Os times de atendimento — para onde o assistente encaminha.
 *
 * 🔴 A descrição de cada time é **o que o modelo lê para escolher o destino**.
 * Editá-la é mudar roteamento, e é por isso que ela tem autor e data no log.
 */
export function Times() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [lista, setLista] = useState<TimeNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)
  const [recado, setRecado] = useState<{ tom: 'sucesso' | 'alerta'; texto: string } | null>(null)
  const [editando, setEditando] = useState<number | null>(null)
  const [rascunho, setRascunho] = useState('')
  const [salvandoDestino, setSalvandoDestino] = useState<number | null>(null)

  const gerencia = pode('ParametroGerenciar')

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.listar())
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function sincronizar() {
    setSincronizando(true)
    setRecado(null)

    try {
      const r = await api.sincronizar()

      // Falha de sincronização não é erro da tela: a lista em cache continua
      // valendo, e o que muda é o aviso — não o conteúdo.
      setRecado(
        r.sucesso
          ? {
              tom: 'sucesso',
              texto: t('times.sincronizado', {
                criados: r.criados,
                atualizados: r.atualizados,
                ausentes: r.ausentes,
              }),
            }
          : { tom: 'alerta', texto: t('times.sincronizacaoFalhou', { erro: r.erro ?? '' }) },
      )

      await carregar()
    } catch {
      setRecado({ tom: 'alerta', texto: t('times.sincronizacaoFalhou', { erro: '' }) })
    } finally {
      setSincronizando(false)
    }
  }

  async function salvarDescricao(time: TimeNaLista) {
    await api.descrever(time.id, rascunho)
    setEditando(null)
    await carregar()
  }

  /**
   * Liga e desliga o time como destino do atendimento automático.
   *
   * 🔴 **Era o campo sem escrita da demanda 141.** Sem esta tela, a correção do
   * padrão vale até a próxima sincronização — um time novo chega e ninguém
   * consegue ajustá-lo.
   */
  async function alternarDestino(time: TimeNaLista) {
    setSalvandoDestino(time.id)
    setRecado(null)

    try {
      await api.definirDestino(time.id, !time.destinoDoAtendimento)
      await carregar()
    } catch {
      setRecado({ tom: 'alerta', texto: t('times.destinoFalhou') })
    } finally {
      setSalvandoDestino(null)
    }
  }

  const ultima = lista?.map((t) => t.sincronizadoEm).filter(Boolean).sort().at(-1) ?? null

  return (
    <>
      <header className="times__topo">
        <div>
          <h1 className="times__titulo">{t('times.titulo')}</h1>
          <p className="times__descricao">{t('times.descricao')}</p>
        </div>

        {gerencia && (
          <Botao variante="primario" onClick={sincronizar} carregando={sincronizando}>
            {t('times.sincronizar')}
          </Botao>
        )}
      </header>

      {/* 🔴 A data da última sincronização fica sempre visível. Lista antiga sem
          aviso é pior que lista vazia: quem olha acha que é a atual. */}
      <p className="times__ultima">
        {ultima
          ? t('times.ultimaSincronizacao', { quando: formatar.dataHora(ultima) })
          : t('times.nuncaSincronizado')}
      </p>

      {recado && <Aviso tom={recado.tom}>{recado.texto}</Aviso>}

      {/* 🔴 O estado que quebra o transbordo dos dois assistentes, dito na tela
          em vez de ficar só no log (demanda 141). Foi assim por sete dias, e
          ninguém tinha como saber olhando o portal. */}
      {lista !== null && lista.length > 0 && !lista.some((x) => x.destinoDoAtendimento) && (
        <Aviso tom="alerta">{t('times.nenhumDestino')}</Aviso>
      )}

      {falhou ? (
        <Estado
          tipo="erro"
          titulo={t('times.erroAoCarregar')}
          acao={<Botao onClick={() => void carregar()}>{t('times.tentarDeNovo')}</Botao>}
        />
      ) : lista === null ? (
        <Estado tipo="carregando" titulo={t('times.carregando')} />
      ) : lista.length === 0 ? (
        <Estado tipo="vazio" titulo={t('times.vazio')} descricao={t('times.vazioAjuda')} />
      ) : (
        <ul className="times__lista">
          {lista.map((time) => (
            <li key={time.id}>
              <Cartao>
                <div className="times__cabecalho">
                  <h2 className="times__nome">
                    {time.nome}
                    {/* Ausente não some da tela: pode haver regra apontando
                        para ele, e sumir deixaria a regra órfã sem aviso. */}
                    {time.ausente && (
                      <span className="times__ausente">{t('times.ausente')}</span>
                    )}

                    {!time.destinoDoAtendimento && !time.ausente && (
                      <span className="times__fora">{t('times.foraDoAutomatico')}</span>
                    )}
                  </h2>

                  {gerencia && editando !== time.id && (
                    <Botao
                      variante="discreto"
                      onClick={() => {
                        setEditando(time.id)
                        setRascunho(time.descricao)
                      }}
                    >
                      {t('times.editarDescricao')}
                    </Botao>
                  )}
                </div>

                {time.ausente && <Aviso tom="alerta">{t('times.ausenteAjuda')}</Aviso>}

                {/* ⚠️ Time ausente não tem opção de destino: ele já não é
                    oferecido ao assistente, e o interruptor sugeriria que
                    ligá-lo o traria de volta. */}
                {!time.ausente && (
                  <label className="times__destino">
                    <input
                      type="checkbox"
                      checked={time.destinoDoAtendimento}
                      disabled={!gerencia || salvandoDestino === time.id}
                      onChange={() => void alternarDestino(time)}
                    />
                    <span>
                      <strong>{t('times.destino')}</strong>
                      <span className="times__ajuda">{t('times.destinoAjuda')}</span>
                    </span>
                  </label>
                )}

                {!time.destinoDoAtendimento && !time.ausente && (
                  <p className="times__ajuda">{t('times.foraDoAutomaticoAjuda')}</p>
                )}

                {editando === time.id ? (
                  <div className="times__edicao">
                    <label className="times__rotulo" htmlFor={`descricao-${time.id}`}>
                      {t('times.descricaoDoTime')}
                    </label>

                    <textarea
                      id={`descricao-${time.id}`}
                      className="times__area"
                      rows={3}
                      value={rascunho}
                      onChange={(e) => setRascunho(e.target.value)}
                    />

                    <p className="times__ajuda">{t('times.descricaoAjuda')}</p>

                    <div className="times__acoes">
                      <Botao variante="primario" onClick={() => void salvarDescricao(time)}>
                        {t('times.salvar')}
                      </Botao>

                      <Botao variante="discreto" onClick={() => setEditando(null)}>
                        {t('times.cancelar')}
                      </Botao>
                    </div>
                  </div>
                ) : (
                  <p className="times__texto">
                    {time.descricao || <em>{t('times.semDescricao')}</em>}
                  </p>
                )}
              </Cartao>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
